import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { useCookieConsent, defaultPreferences, type CookiePreferences } from './CookieConsent';

export default function CookieBanner() {
  const { consent, bannerOpen, modalOpen, acceptAll, rejectAll, savePreferences, openModal, closeModal } =
    useCookieConsent();

  const [draft, setDraft] = useState<CookiePreferences>(defaultPreferences);

  useEffect(() => {
    if (modalOpen) setDraft(consent?.preferences ?? defaultPreferences);
  }, [modalOpen, consent]);

  if (!bannerOpen && !modalOpen) return null;

  return (
    <>
      {bannerOpen && (
        <div
          role="dialog"
          aria-live="polite"
          className="fixed bottom-4 right-4 z-50 w-[calc(100%-2rem)] max-w-sm rounded-2xl border border-slate-200/80 bg-white/95 p-5 shadow-2xl backdrop-blur transition-all duration-300 sm:bottom-6 sm:right-6 sm:max-w-md"
        >
          <div className="flex flex-col gap-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="font-semibold text-slate-900">We value your privacy</p>
                <p className="mt-1 text-sm leading-relaxed text-slate-600">
                  We use cookies to improve your experience. Choose what you are comfortable with.
                </p>
              </div>
              <button
                onClick={rejectAll}
                className="rounded-full p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
                aria-label="Close and use necessary cookies only"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
              <button
                onClick={openModal}
                className="rounded-full px-4 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-100 hover:text-slate-900"
              >
                Manage cookies
              </button>
              <div className="flex gap-2">
                <button
                  onClick={rejectAll}
                  className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                >
                  Reject
                </button>
                <button
                  onClick={acceptAll}
                  className="rounded-full bg-brand-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-brand-700"
                >
                  Accept all
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-slate-900/40 p-4 backdrop-blur-sm transition-all duration-300 sm:items-center">
          <div
            className="w-full max-w-md rounded-3xl border border-slate-200 bg-white p-6 shadow-2xl sm:p-8"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cookie-title"
          >
            <div className="flex items-center justify-between">
              <h2 id="cookie-title" className="text-xl font-bold text-slate-900">Cookie preferences</h2>
              <button onClick={closeModal} className="rounded-full p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600" aria-label="Close">
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">
              Manage how we use cookies. Necessary cookies are always on because they keep the site working.
            </p>
            <div className="mt-6 space-y-3">
              {[
                { key: 'necessary' as const, label: 'Necessary', desc: 'Essential for the site to work. Cannot be turned off.' },
                { key: 'functional' as const, label: 'Functional', desc: 'Remember preferences and make the experience better.' },
                { key: 'analytics' as const, label: 'Analytics', desc: 'Help us understand how visitors use the site.' },
                { key: 'marketing' as const, label: 'Marketing', desc: 'Used to deliver relevant ads and measure campaigns.' },
              ].map((c) => (
                <label
                  key={c.key}
                  className="flex cursor-pointer items-center justify-between rounded-2xl border border-slate-100 bg-slate-50 p-4 transition hover:bg-slate-100"
                >
                  <div>
                    <span className="font-semibold text-slate-900">{c.label}</span>
                    <p className="mt-0.5 text-xs text-slate-600">{c.desc}</p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={draft[c.key]}
                    disabled={c.key === 'necessary'}
                    onClick={() => setDraft({ ...draft, [c.key]: !draft[c.key] })}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition focus:outline-none ${
                      draft[c.key] ? 'bg-brand-600' : 'bg-slate-300'
                    } ${c.key === 'necessary' ? 'cursor-not-allowed opacity-70' : ''}`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                        draft[c.key] ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </label>
              ))}
            </div>
            <div className="mt-8 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <button
                onClick={rejectAll}
                className="rounded-full bg-slate-100 px-6 py-2.5 text-sm font-bold text-slate-700 transition hover:bg-slate-200"
              >
                Reject all
              </button>
              <button
                onClick={() => savePreferences(draft)}
                className="rounded-full bg-brand-600 px-6 py-2.5 text-sm font-bold text-white transition hover:bg-brand-700"
              >
                Save preferences
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
